import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  IAuthenticatedPropertyDetails,
  IProperty,
  IPropertyDetails,
  IPropertyPhoto,
  IPropertyResponse,
  IPropertySearchParameters,
  IUploadImageResponse,
  PropertyState
} from './types';
import { httpClient } from '@lib/httpClient';
import { PHOTOS_DOMAIN } from '../../config';
import { useUserStore } from '@stores/User/User';

// Normaliza uma URL de imagem relativa para absoluta
const normalizeImageUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;

  const cleaned = url.startsWith('/') ? url.substring(1) : url;
  return `${PHOTOS_DOMAIN}${cleaned}`;
};

// Aplica normalização na capa de um imóvel
const normalizePropertyCover = <T extends { foto_capa: string }>(
  property: T
): T => ({
  ...property,
  foto_capa: normalizeImageUrl(property.foto_capa) ?? property.foto_capa
});

// Aplica normalização nas fotos de galeria (campo `uri`)
const normalizeGalleryPhotos = <T extends { fotos_galeria: { uri: string }[] }>(
  data: T
): T => ({
  ...data,
  fotos_galeria: data.fotos_galeria.map((foto) => ({
    ...foto,
    uri: normalizeImageUrl(foto.uri) ?? foto.uri
  }))
});

// Aplica normalização no avatar do corretor (quando existir)
const normalizeAgentAvatar = <
  T extends { corretor?: { avatar?: string | null } }
>(
  data: T
): T => {
  if (!data.corretor) return data;

  return {
    ...data,
    corretor: {
      ...data.corretor,
      avatar:
        normalizeImageUrl(data.corretor.avatar) ??
        data.corretor.avatar ??
        undefined
    }
  };
};

const toNullableForeignKey = (value: unknown): number | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null || value === '' || value === 0 || value === '0') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNullableNumber = (value: unknown): number | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null || value === '') return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toRecord = (value: unknown): Record<string, unknown> =>
  (value as unknown as Record<string, unknown>) || {};

const normalizePropertyPayloadForApi = (
  payload: Partial<IAuthenticatedPropertyDetails>
): Partial<IAuthenticatedPropertyDetails> => {
  const normalized: Partial<IAuthenticatedPropertyDetails> = { ...payload };

  const rootCodImovelId = toNullableForeignKey(normalized.cod_imovel_id);
  if (rootCodImovelId !== undefined) {
    normalized.cod_imovel_id = rootCodImovelId;
  }

  const rootCorretorId = toNullableForeignKey(
    (normalized as Record<string, unknown>).corretor_id
  );
  if (rootCorretorId !== undefined) {
    (normalized as Record<string, unknown>).corretor_id = rootCorretorId;
  }

  (['valor_venda', 'valor_aluguel', 'valor_substituto'] as const).forEach(
    (key) => {
      const nextValue = toNullableNumber(normalized[key]);
      if (nextValue !== undefined) {
        normalized[key] = nextValue;
      }
    }
  );

  if (normalized.dados_localizacao) {
    const dadosLocalizacao = {
      ...toRecord(normalized.dados_localizacao)
    };

    const cidadeId = toNullableForeignKey(dadosLocalizacao.cidade_id);
    if (cidadeId !== undefined) {
      dadosLocalizacao.cidade_id = cidadeId;
    }

    const estadoId = toNullableForeignKey(dadosLocalizacao.estado_id);
    if (estadoId !== undefined) {
      dadosLocalizacao.estado_id = estadoId;
    }

    (normalized as Record<string, unknown>).dados_localizacao =
      dadosLocalizacao;
  }

  if (normalized.dados_comercializacao) {
    const dadosComercializacao = {
      ...toRecord(normalized.dados_comercializacao)
    };

    const condicaoId = toNullableForeignKey(dadosComercializacao.condicao_id);
    if (condicaoId !== undefined) {
      dadosComercializacao.condicao_id = condicaoId;
    }

    const chaveId = toNullableForeignKey(dadosComercializacao.chave_id);
    if (chaveId !== undefined) {
      dadosComercializacao.chave_id = chaveId;
    }

    const tipoImovelId = toNullableForeignKey(
      dadosComercializacao.tipoimovel_id
    );
    if (tipoImovelId !== undefined) {
      dadosComercializacao.tipoimovel_id = tipoImovelId;
    }

    const nestedTipoImovel = toRecord(dadosComercializacao.tipo_imovel);
    const nestedTitulo =
      typeof nestedTipoImovel.titulo === 'string'
        ? nestedTipoImovel.titulo.trim()
        : '';
    const topLevelTipoImovel = toRecord(normalized.tipo_imovel);
    const topLevelTitulo =
      typeof topLevelTipoImovel.titulo === 'string'
        ? String(topLevelTipoImovel.titulo).trim()
        : '';

    if (!nestedTitulo && topLevelTitulo) {
      dadosComercializacao.tipo_imovel = {
        ...nestedTipoImovel,
        titulo: topLevelTitulo
      };
    }

    (normalized as Record<string, unknown>).dados_comercializacao =
      dadosComercializacao;
  }

  if (normalized.dados_privados) {
    const dadosPrivados = {
      ...toRecord(normalized.dados_privados)
    };

    (
      [
        'valor_condominio',
        'valor_proprietario',
        'valor_iptu',
        'comissao',
        'porcentagem_comissao'
      ] as const
    ).forEach((key) => {
      const nextValue = toNullableNumber(dadosPrivados[key]);
      if (nextValue !== undefined) {
        dadosPrivados[key] = nextValue;
      }
    });

    (normalized as Record<string, unknown>).dados_privados = dadosPrivados;
  }

  return normalized;
};

export const usePropertyStore = create<PropertyState>()(
  devtools((set, get) => ({
    searchProperties: async (query: IPropertySearchParameters) => {
      try {
        const endpoint = '/api/imoveis';
        const { data }: { data: IPropertyResponse } = await httpClient.get(
          endpoint,
          { params: query }
        );

        data.data = data.data.map((property) =>
          normalizePropertyCover(property)
        );

        return data;
      } catch {
        return null;
      }
    },

    searchAuthenticatedProperties: async (query: IPropertySearchParameters) => {
      try {
        const { token } = useUserStore.getState();
        if (!token) return null;
        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        const endpoint = '/api/imoveis/pesquisar';
        const { data }: { data: IPropertyResponse } = await httpClient.get(
          endpoint,
          { params: query }
        );

        data.data = data.data.map((property) =>
          normalizePropertyCover(property)
        );

        return data;
      } catch {
        return null;
      }
    },
    fetchPropertyDetails: async (id: number) => {
      try {
        const endpoint = `/api/imoveis/publico/detalhe`;
        const { data }: { data: IPropertyDetails | null } =
          await httpClient.get(endpoint, { params: { id } });

        if (!data) {
          return null;
        }

        const normalized = normalizeAgentAvatar(
          normalizeGalleryPhotos(normalizePropertyCover(data))
        );

        return normalized;
      } catch {
        return null;
      }
    },
    fetchPrivatePropertyDetails: async (id: number) => {
      try {
        const { token } = useUserStore.getState();
        if (!token) return null;
        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        const endpoint = `/api/imoveis/privado/detalhe`;
        const { data }: { data: IPropertyDetails | null } =
          await httpClient.get(endpoint, { params: { id } });

        if (!data) return null;

        const normalized = normalizeAgentAvatar(
          normalizeGalleryPhotos(normalizePropertyCover(data))
        );

        return normalized;
      } catch {
        return null;
      }
    },
    fetchFeaturedProperties: async () => {
      try {
        const endpoint = '/api/imoveis/destaques';
        const { data }: { data: IProperty[] } = await httpClient.get(endpoint);

        const normalized = data.map((property) =>
          normalizePropertyCover(property)
        );
        return normalized;
      } catch {
        return [];
      }
    },
    fetchAuthenticatedPropertyDetails: async (id: number) => {
      try {
        const { token } = useUserStore.getState();
        if (!token) {
          return null;
        }
        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        const endpoint = `/api/imoveis/${id}`;
        const { data }: { data: IAuthenticatedPropertyDetails } =
          await httpClient.get(endpoint);

        const normalized = normalizeAgentAvatar(
          normalizeGalleryPhotos(normalizePropertyCover(data))
        );

        return normalized;
      } catch {
        return null;
      }
    },
    fetchPaginatedFeaturedProperties: async (
      query: IPropertySearchParameters
    ) => {
      try {
        // Temporary hack to reuse existing method
        const page = query.page || 1;
        const unpaginatedProperties = get().fetchFeaturedProperties();
        const startIndex = (page - 1) * 9;
        const paginatedData = (await unpaginatedProperties).slice(
          startIndex,
          startIndex + 9
        );

        return {
          current_page: page,
          last_page: Math.ceil((await unpaginatedProperties).length / 9),
          per_page: 9,
          total: (await unpaginatedProperties).length,
          data: paginatedData
        };
      } catch {
        return null;
      }
    },

    fetchUnpublishedProperties: async (query: IPropertySearchParameters) => {
      try {
        const { token } = useUserStore.getState();
        if (!token) {
          return null;
        }
        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        const endpoint = `/api/imoveis/nao-publicado`;
        const { data }: { data: IPropertyResponse } = await httpClient.get(
          endpoint,
          { params: query }
        );

        data.data = data.data.map((property) =>
          normalizePropertyCover(property)
        );

        return data;
      } catch {
        return null;
      }
    },
    fetchNegotiatingProperties: async (query: IPropertySearchParameters) => {
      try {
        const { token } = useUserStore.getState();
        if (!token) {
          return null;
        }
        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        const endpoint = `/api/imoveis/negotiating`;
        const { data }: { data: IPropertyResponse } = await httpClient.get(
          endpoint,
          { params: query }
        );

        data.data = data.data.map((property) =>
          normalizePropertyCover(property)
        );

        return data;
      } catch {
        return null;
      }
    },
    fetchInactiveProperties: async (query: IPropertySearchParameters) => {
      try {
        const { token } = useUserStore.getState();
        if (!token) {
          return null;
        }
        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        const endpoint = `/api/imoveis/inativo`;
        const { data }: { data: IPropertyResponse } = await httpClient.get(
          endpoint,
          { params: query }
        );

        data.data = data.data.map((property) =>
          normalizePropertyCover(property)
        );

        return data;
      } catch {
        return null;
      }
    },

    fetchArchivedProperties: async (query: IPropertySearchParameters) => {
      try {
        const { token } = useUserStore.getState();
        if (!token) {
          return null;
        }
        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        const endpoint = `/api/imoveis/arquivados`;
        const { data }: { data: IPropertyResponse } = await httpClient.get(
          endpoint,
          { params: query }
        );

        data.data = data.data.map((property) =>
          normalizePropertyCover(property)
        );

        return data;
      } catch {
        return null;
      }
    },

    fetchSoldProperties: async (query: IPropertySearchParameters) => {
      try {
        const { token } = useUserStore.getState();
        if (!token) {
          return null;
        }
        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        const endpoint = `/api/imoveis/vendidos`;
        const { data }: { data: IPropertyResponse } = await httpClient.get(
          endpoint,
          { params: query }
        );

        data.data = data.data.map((property) =>
          normalizePropertyCover(property)
        );

        return data;
      } catch {
        return null;
      }
    },

    fetchRentedProperties: async (query: IPropertySearchParameters) => {
      try {
        const { token } = useUserStore.getState();
        if (!token) {
          return null;
        }
        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        const endpoint = `/api/imoveis/alugados`;
        const { data }: { data: IPropertyResponse } = await httpClient.get(
          endpoint,
          { params: query }
        );

        data.data = data.data.map((property) =>
          normalizePropertyCover(property)
        );

        return data;
      } catch {
        return null;
      }
    },

    postNewProperty: async (
      payload: Partial<IAuthenticatedPropertyDetails>
    ) => {
      try {
        const { token } = useUserStore.getState();
        if (!token) {
          return {
            success: false,
            status: 401,
            message: 'Unauthorized'
          };
        }

        const outgoingPayload: Partial<IAuthenticatedPropertyDetails> = {
          ...payload
        };

        if (!!outgoingPayload.fotos_galeria?.length) {
          const updatedPhotos = await get().uploadPropertyPhotos(
            outgoingPayload.fotos_galeria
          );
          if (!updatedPhotos) {
            return {
              success: false,
              message: 'Falha ao enviar imagens.',
              isImageUploadError: true
            };
          }

          outgoingPayload.fotos_galeria = updatedPhotos || [];
          outgoingPayload.foto_capa =
            updatedPhotos?.find((photo) => photo.is_cover)?.uri ||
            updatedPhotos?.[0]?.uri ||
            '';
        }

        const sanitizedPayload =
          normalizePropertyPayloadForApi(outgoingPayload);

        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        const endpoint = `/api/imoveis`;
        await httpClient.post(endpoint, sanitizedPayload);
        return { success: true };
      } catch (error) {
        const maybeError = error as {
          response?: {
            status?: number;
            data?: {
              message?: string;
              errors?: Record<string, string[]>;
            };
          };
        };

        if (maybeError.response?.status === 422) {
          console.error(
            'POST /api/imoveis validation error:',
            maybeError.response.data
          );
        }

        return {
          success: false,
          status: maybeError.response?.status,
          message:
            maybeError.response?.data?.message || 'Erro ao salvar imóvel.',
          errors: maybeError.response?.data?.errors,
          isImageUploadError: false
        };
      }
    },

    patchEditProperty: async (
      id: number,
      payload: Partial<IAuthenticatedPropertyDetails>
    ) => {
      try {
        const { token } = useUserStore.getState();
        if (!token) {
          return {
            success: false,
            status: 401,
            message: 'Unauthorized'
          };
        }

        // Decide whether to send fotos_galeria:
        // - If there are photos with `file`, upload and send
        // - If array is empty OR contains no `file`, omit from payload
        const outgoingPayload: Partial<IAuthenticatedPropertyDetails> = {
          ...payload
        };

        const gallery = payload.fotos_galeria || [];
        const hasNewFiles = gallery.some((p) => !!p.file);
        const hasAnyPhotos = gallery.length > 0;

        if (hasNewFiles) {
          const updatedPhotos = await get().uploadPropertyPhotos(gallery);
          if (!updatedPhotos) {
            return {
              success: false,
              message: 'Falha ao enviar imagens.',
              isImageUploadError: true
            };
          }
          outgoingPayload.fotos_galeria = updatedPhotos || [];
          outgoingPayload.foto_capa =
            updatedPhotos?.find((photo) => photo.is_cover)?.uri ||
            updatedPhotos?.[0]?.uri ||
            payload.foto_capa ||
            '';
        } else {
          // No new files; if array is empty, omit to avoid clearing on backend
          // If array has items but none with `file`, we assume no change and omit
          if (!hasAnyPhotos) {
            // No gallery at all: omit to avoid backend clearing
            delete outgoingPayload.fotos_galeria;
          } else {
            // Reorder-only: send gallery with current ordem/flags so backend persists
            outgoingPayload.fotos_galeria = gallery.map((p) => ({
              id: p.id,
              imovel_id: p.imovel_id,
              uri: p.uri,
              ordem: p.ordem ?? 0,
              active: typeof p.active === 'boolean' ? p.active : true,
              legenda: p.legenda ?? ''
            }));
            // A capa é sempre a primeira foto por ordem
            const sortedGallery = [...gallery].sort(
              (a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)
            );
            outgoingPayload.foto_capa =
              sortedGallery[0]?.uri ?? payload.foto_capa ?? '';
          }
        }

        const sanitizedPayload =
          normalizePropertyPayloadForApi(outgoingPayload);

        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        const endpoint = `/api/editar_imoveis/${id}`;
        await httpClient.patch(endpoint, sanitizedPayload);
        return { success: true };
      } catch (error) {
        const maybeError = error as {
          response?: {
            status?: number;
            data?: {
              message?: string;
              errors?: Record<string, string[]>;
            };
          };
        };

        if (maybeError.response?.status === 422) {
          console.error(
            'PATCH /api/editar_imoveis validation error:',
            maybeError.response.data
          );
        }

        return {
          success: false,
          status: maybeError.response?.status,
          message:
            maybeError.response?.data?.message || 'Erro ao atualizar imóvel.',
          errors: maybeError.response?.data?.errors,
          isImageUploadError: false
        };
      }
    },

    uploadPropertyPhotos: async (photos: IPropertyPhoto[]) => {
      try {
        const { token } = useUserStore.getState();
        if (!token || photos.length === 0) {
          return null;
        }
        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        const endpoint = `/api/imoveis/upload/imagem`;
        const BATCH_SIZE = 5;
        const photosToUpload = photos.filter((photo) => !!photo.file);
        const uploadedUrlsByPhotoId = new Map<number, string>();

        for (let i = 0; i < photosToUpload.length; i += BATCH_SIZE) {
          const batch = photosToUpload.slice(i, i + BATCH_SIZE);
          const formData = new FormData();

          batch.forEach((photo) => {
            formData.append('images[]', photo.file as unknown as File);
          });

          const { data }: { data: IUploadImageResponse } =
            await httpClient.post(endpoint, formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              },
              timeout: 240000 * (1 + batch.length)
            });

          batch.forEach((photo, index) => {
            const uploadedUrl = data.urls[index];
            if (uploadedUrl) {
              uploadedUrlsByPhotoId.set(photo.id, uploadedUrl);
            }
          });
        }

        photosToUpload.forEach((photo) => {
          const uploadedUrl = uploadedUrlsByPhotoId.get(photo.id);
          if (uploadedUrl) {
            photo.uri = uploadedUrl;
          }
        });

        const updatedPhotos = photos.map((photo) => {
          const updated = photosToUpload.find((p) => p.id === photo.id);
          return updated ? updated : photo;
        });

        return updatedPhotos;
      } catch {
        return null;
      }
    },
    approveProperty: async (id: number) => {
      try {
        const { token } = useUserStore.getState();
        if (!token) {
          return false;
        }
        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        const endpoint = `/api/imoveis/${id}/publish`;
        await httpClient.patch(endpoint);
        return true;
      } catch {
        return false;
      }
    },
    checkCanEdit: async (id: number) => {
      try {
        const { token } = useUserStore.getState();
        if (!token) {
          return { can_edit: false, reason: 'Não autenticado.' };
        }
        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        const { data } = await httpClient.get(`/api/imoveis/${id}/pode-editar`);
        return data;
      } catch {
        return { can_edit: false, reason: 'Erro ao verificar permissão.' };
      }
    },
    fetchAllFeaturedProperties: async () => {
      try {
        const { token } = useUserStore.getState();
        if (!token) return [];
        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        const { data }: { data: IProperty[] } = await httpClient.get(
          '/api/imoveis/destaques/todos'
        );
        return data.map((property) => normalizePropertyCover(property));
      } catch {
        return [];
      }
    },
    reordenarDestaques: async (ids: number[]) => {
      try {
        const { token } = useUserStore.getState();
        if (!token) return false;
        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        await httpClient.patch('/api/imoveis/reordenardestaques', { ids });
        return true;
      } catch {
        return false;
      }
    },
    removerDestaque: async (id: number) => {
      try {
        const { token } = useUserStore.getState();
        if (!token) return false;
        httpClient.defaults.headers.Authorization = `Bearer ${token}`;
        await httpClient.patch(`/api/imoveis/${id}/removerdestaque`);
        return true;
      } catch {
        return false;
      }
    }
  }))
);
